import { useEffect, useRef } from "react";
import { Box } from "react-bulma-components";
import ChannelConnection from "../../js/connection/ChannelConnection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";

type Props = {
    connection: ChannelConnection | undefined,
};

export default function ChatBox({ connection }: Props) {
    const chatBox = useRef<HTMLDivElement>(null);
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        function writeMessage({ user, message }: {user: string, message: string}) {
            // Check if message is JSON or contains action
            try {
                const parsedMessage = JSON.parse(message);
                //console.log('Skipping JSON message:', parsedMessage);
                return; // Skip JSON messages
            } catch (e) {
                // Check if message contains action parameter without being valid JSON
                if (message.includes('"action"') || message.includes("'action'")) {
                    //console.log('Skipping message with action parameter:', message);
                    return;
                }
                // Not JSON or action message, continue with normal message display
            }
            
            const content = document.createElement("div");
            const isCurrentUser = user === connection?.channel.getUserAlias(); // Use channel's client ID to identify current user
            const currentTime = new Date().toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit'
            });
    
            content.className = `message ${isCurrentUser ? 'sent' : 'received'}`;
            content.innerHTML = `
                <div class="message-content">
                    <div class="message-sender">${user}</div>
                    <div class="message-bubble">
                        <span class="message-text">${message}</span>
                        <div style="text-align: right;" class="message-time">${currentTime}</div>
                    </div>
                </div>
            `;
            
            if (chatBox.current !== null) {
                chatBox.current.appendChild(content);
                chatBox.current.scrollTop = chatBox.current.scrollHeight;
            }
        }

        document.addEventListener("keydown", onKeydown);
        connection?.events.addListener("onmessage", writeMessage);
        return () => {
            document.removeEventListener("keydown", onKeydown);
            connection?.events.removeListener("onmessage", writeMessage);
        }
    });

    function onKeydown(event: KeyboardEvent) {
        if (event.key === "Enter") {
            onSendClicked();
        }
    }

    function onSendClicked() {        
        let currentMessage: string = input.current!.value;

        if (currentMessage) {
            connection?.messager.sendChatMessage(currentMessage)?.then(() => {
                currentMessage = "";
                input.current!.value = "";
            });
        }
    }

    return (
        <Box style={{ 
            display: "flex", 
            flexDirection: "column", 
            height: "100%", 
            borderRadius: 12,
            backgroundColor: "#ffffff",
            boxShadow: "none",
            border: "none",
            padding: 0
        }}>
            <div 
                ref={chatBox} 
                style={{ 
                    flex: 1, 
                    padding: "1rem",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem"
                }}
            >
                {/* Paragraphs will go here */}
            </div>

            <div style={{ 
                padding: "1rem",
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                gap: "0.5rem"
            }}>
                <input 
                    ref={input} 
                    className="chat-input" 
                    type="text" 
                    placeholder="Enter a message" 
                    style={{
                        flex: 1,
                        padding: "0.75rem 1rem",
                        borderRadius: 20,
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        fontSize: "1rem",
                        outline: "none",
                        transition: "all 0.2s ease"
                    }}
                />
                <button 
                    type="button" 
                    className="send-button" 
                    onClick={onSendClicked}
                    style={{
                        width: "2.5rem",
                        height: "2.5rem",
                        borderRadius: "50%",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                    }}
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>

            <style>{`
                .chat-input:focus {
                    border-color: #007AFF;
                    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
                }

                .send-button:hover {
                    background: #f2f2f7;
                    transform: translateY(-1px);
                }

                .send-button:active {
                    background: #e5e5ea;
                    transform: translateY(0);
                }

                .message {
                    // display: flex;
                    margin-bottom: 0rem !important;
                    max-width: 80%;
                    width: 100%;
                }

                .message.sent {
                    align-self: flex-end;
                    justify-content: flex-end;
                }

                .message.received {
                    align-self: flex-start;
                    justify-content: flex-start;
                }

                .message-content {
                    // display: flex;
                    // flex-direction: column;
                    max-width: 100%;
                }

                .message-sender {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #007AFF;
                    margin-bottom: 0.25rem;
                    padding-left: 0.5rem;
                }

                .message-bubble {
                    // display: inline-flex;
                    // align-items: flex-end;
                    max-width: 100%;
                    position: relative;
                }

                .message.sent .message-bubble {
                    flex-direction: row-reverse;
                }

                .message.sent .message-content {
                    align-items: flex-end;
                }

                .message.received .message-content {
                    align-items: flex-start;
                }

                .message-text {
                    font-size: 0.9375rem;
                    line-height: 1.4;
                    color: #000;
                    padding: 0.5rem 0.75rem;
                    border-radius: 7.5px;
                    max-width: calc(100% - 3rem);
                    word-break: break-word;
                }

                .message.sent .message-text {
                    border-bottom-right-radius: 0;
                }
                .sent{
                    background: #DCF8C6;
                }

                .message.received .message-text {
                    background: #f2f2f7;
                    border-bottom-left-radius: 0;
                }

                .message-time {
                    font-size: 0.6875rem;
                    color: rgba(0, 0, 0, 0.45);
                    margin: 0 0.5rem 0.25rem;
                    white-space: nowrap;
                }

                @media (max-width: 768px) {
                    .chat-input {
                        font-size: 0.9375rem;
                        padding: 0.625rem 0.875rem;
                    }

                    .send-button {
                        width: 2.25rem;
                        height: 2.25rem;
                    }

                    .message {
                        max-width: 85%;
                    }
                }
            `}</style>
        </Box>
    );
}
